import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AppShell } from './components/AppShell';
import { RequireAuth } from './components/RequireAuth';
import { SkeletonRows } from './components/ui/States';
import { Toaster } from './components/ui/Toaster';
import { createQueryClient } from './lib/query';
import { AlarmsPage } from './pages/AlarmsPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';

/*
 * The alarm list, the dashboard and sign-in are what people open; everything
 * else is split out so the first paint on a phone stays small.
 */
const AlarmDetailPage = lazy(() =>
  import('./pages/AlarmDetailPage').then((m) => ({ default: m.AlarmDetailPage })),
);
const ApiKeysPage = lazy(() =>
  import('./pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
);
const ContactsPage = lazy(() =>
  import('./pages/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const EscalationsPage = lazy(() =>
  import('./pages/EscalationsPage').then((m) => ({ default: m.EscalationsPage })),
);
const IntegrationDetailPage = lazy(() =>
  import('./pages/IntegrationDetailPage').then((m) => ({ default: m.IntegrationDetailPage })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const MaintenancePage = lazy(() =>
  import('./pages/MaintenancePage').then((m) => ({ default: m.MaintenancePage })),
);
const OutboundPage = lazy(() =>
  import('./pages/OutboundPage').then((m) => ({ default: m.OutboundPage })),
);
const PoliciesPage = lazy(() =>
  import('./pages/PoliciesPage').then((m) => ({ default: m.PoliciesPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const RulesPage = lazy(() => import('./pages/RulesPage').then((m) => ({ default: m.RulesPage })));
const SchedulesPage = lazy(() =>
  import('./pages/SchedulesPage').then((m) => ({ default: m.SchedulesPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TeamsPage = lazy(() => import('./pages/TeamsPage').then((m) => ({ default: m.TeamsPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));

const queryClient = createQueryClient();

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<SkeletonRows rows={6} />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/alarms" element={<AlarmsPage />} />
                <Route path="/alarms/:id" element={<AlarmDetailPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="/integrations/:id" element={<IntegrationDetailPage />} />
                <Route path="/outbound" element={<OutboundPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/rules" element={<RulesPage />} />
                <Route path="/policies" element={<PoliciesPage />} />
                <Route path="/escalations" element={<EscalationsPage />} />
                <Route path="/schedules" element={<SchedulesPage />} />
                <Route path="/schedules/:id" element={<SchedulesPage />} />
                <Route path="/maintenance" element={<MaintenancePage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/api-keys" element={<ApiKeysPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  );
}
